/**
 * DebugLogProvider
 *
 * Captures every axios call into a ring buffer the Developer Dock
 * renders. Only attaches the interceptors when the signed-in user has
 * one of the dev-dock roles, so anonymous + non-dev sessions pay zero
 * runtime cost.
 *
 * Sensitive data masking
 *   Request bodies, response bodies, and headers all go through
 *   `maskSensitive` before being stashed, which scrubs anything keyed
 *   like a password / API key / token / Authorization header. The
 *   dock surfaces enough to debug — never the literal secret.
 */
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { apiClient } from '@/shared/api';
import { useAuth } from '@/app/providers';
import { DEV_DOCK_ROLES } from '@/types/entities';
import { DebugLogContext, type DebugLogContextValue, type DebugLogEntry } from './debugLogContext';

// ─────────────────────────────────────────────────────────────────────
// Tuneables
// ─────────────────────────────────────────────────────────────────────

const MAX_ENTRIES = 50;
// Keys whose values we redact in logged headers / bodies. Matched
// case-insensitively against object keys + Authorization header value.
const SENSITIVE_KEY_RE =
    /^(?:authorization|password|api[_-]?key|access[_-]?token|refresh[_-]?token|client[_-]?secret|secret|bearer)$/i;

// ─────────────────────────────────────────────────────────────────────
// Pure helpers — exported for unit tests if we add them later
// ─────────────────────────────────────────────────────────────────────

function maskSensitive(value: unknown, depth = 0): unknown {
    if (value == null || depth > 6) return value;
    if (Array.isArray(value)) {
        return value.map((v) => maskSensitive(v, depth + 1));
    }
    if (typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
            if (SENSITIVE_KEY_RE.test(key)) {
                out[key] = typeof v === 'string' ? `••• (${v.length} chars)` : '•••';
            } else {
                out[key] = maskSensitive(v, depth + 1);
            }
        }
        return out;
    }
    return value;
}

function maskHeaders(rawHeaders: unknown): Record<string, string> {
    const result: Record<string, string> = {};
    if (rawHeaders == null || typeof rawHeaders !== 'object') return result;
    for (const [key, value] of Object.entries(rawHeaders as Record<string, unknown>)) {
        if (value == null) continue;
        const stringValue = String(value);
        if (SENSITIVE_KEY_RE.test(key)) {
            // For the Authorization header specifically we surface the
            // scheme (Bearer / Basic) so the dock can show "Bearer •••"
            // — useful for spotting "no token attached" bugs.
            const scheme = stringValue.split(' ')[0];
            result[key] = scheme ? `${scheme} •••` : '•••';
        } else {
            result[key] = stringValue;
        }
    }
    return result;
}

const makeId = (): string =>
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// ─────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────

interface DebugLogProviderProps {
    children: ReactNode;
}

export function DebugLogProvider({ children }: DebugLogProviderProps) {
    const { user, isAuthenticated } = useAuth();
    const enabled = useMemo(() => {
        if (!isAuthenticated || !user) return false;
        const role = (user.role || '').toLowerCase();
        return DEV_DOCK_ROLES.includes(role as (typeof DEV_DOCK_ROLES)[number]);
    }, [isAuthenticated, user]);

    const [entries, setEntries] = useState<DebugLogEntry[]>([]);
    const [paused, setPaused] = useState(false);
    const [dockOpen, setDockOpen] = useState(false);
    // Stable refs so the interceptor closures don't capture stale state.
    const pausedRef = useRef(paused);
    useEffect(() => {
        pausedRef.current = paused;
    }, [paused]);
    // Maps request config IDs → entry IDs so the response interceptor
    // can find the in-flight entry it needs to complete.
    const inFlightRef = useRef<Map<string, { entryId: string; startedAt: number }>>(new Map());

    const appendEntry = useCallback((entry: DebugLogEntry) => {
        if (pausedRef.current) return;
        setEntries((prev) => {
            const next = [entry, ...prev];
            if (next.length > MAX_ENTRIES) next.length = MAX_ENTRIES;
            return next;
        });
    }, []);

    const finalizeEntry = useCallback(
        (entryId: string, patch: Partial<DebugLogEntry>) => {
            setEntries((prev) =>
                prev.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry)),
            );
        },
        [],
    );

    const togglePause = useCallback(() => setPaused((p) => !p), []);
    const clear = useCallback(() => setEntries([]), []);

    // Attach axios interceptors only while enabled — when the user
    // logs out or their role changes we eject them so an anonymous
    // visitor's apiClient is back to its lean state.
    useEffect(() => {
        if (!enabled) return;

        const requestId = apiClient.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                try {
                    const correlationId = makeId();
                    const startedAt = performance.now();
                    // Stash on the config so the response interceptor
                    // can find us. `__debugId` is a custom prop, not
                    // part of Axios — it survives the round-trip.
                    (config as unknown as { __debugId: string }).__debugId = correlationId;
                    inFlightRef.current.set(correlationId, { entryId: correlationId, startedAt });

                    appendEntry({
                        id: correlationId,
                        timestamp: Date.now(),
                        method: (config.method || 'get').toUpperCase(),
                        url:
                            (config.baseURL || '') +
                            (config.url || '') +
                            (config.params ? `?${new URLSearchParams(config.params as Record<string, string>).toString()}` : ''),
                        requestHeaders: maskHeaders(config.headers),
                        requestBody: maskSensitive(config.data),
                        status: 'pending',
                        statusCode: null,
                        responseBody: null,
                        durationMs: null,
                        errorMessage: null,
                    });
                } catch {
                    /* never let the dock break the actual request */
                }
                return config;
            },
            (error) => Promise.reject(error),
        );

        const responseId = apiClient.interceptors.response.use(
            (response: AxiosResponse) => {
                try {
                    const correlationId = (response.config as unknown as { __debugId?: string }).__debugId;
                    if (!correlationId) return response;
                    const inFlight = inFlightRef.current.get(correlationId);
                    inFlightRef.current.delete(correlationId);
                    finalizeEntry(correlationId, {
                        status: 'ok',
                        statusCode: response.status,
                        responseBody: maskSensitive(response.data),
                        durationMs: inFlight ? Math.round(performance.now() - inFlight.startedAt) : null,
                    });
                } catch {
                    /* swallow — debug should never throw */
                }
                return response;
            },
            (error: AxiosError) => {
                try {
                    const config = error.config as InternalAxiosRequestConfig & { __debugId?: string };
                    const correlationId = config?.__debugId;
                    if (correlationId) {
                        const inFlight = inFlightRef.current.get(correlationId);
                        inFlightRef.current.delete(correlationId);
                        finalizeEntry(correlationId, {
                            status: 'error',
                            statusCode: error.response?.status ?? null,
                            responseBody: maskSensitive(error.response?.data),
                            durationMs: inFlight ? Math.round(performance.now() - inFlight.startedAt) : null,
                            errorMessage: error.message,
                        });
                    }
                } catch {
                    /* swallow */
                }
                return Promise.reject(error);
            },
        );

        return () => {
            apiClient.interceptors.request.eject(requestId);
            apiClient.interceptors.response.eject(responseId);
            inFlightRef.current.clear();
        };
    }, [enabled, appendEntry, finalizeEntry]);

    const value = useMemo<DebugLogContextValue>(
        () => ({ entries, paused, enabled, dockOpen, setDockOpen, togglePause, clear }),
        [entries, paused, enabled, dockOpen, togglePause, clear],
    );

    return <DebugLogContext.Provider value={value}>{children}</DebugLogContext.Provider>;
}
